/**
 * Application du thème choisi par l'utilisateur (`user_settings.theme`).
 *
 * Sans ce module, le réglage serait décoratif : Tailwind fait varier `dark:`
 * sur `prefers-color-scheme` seul, donc « clair » et « sombre » ne changeraient
 * rien à l'écran. On pose la variante nous-mêmes sur `<html data-theme>`, et
 * `index.css` la déclare (`@custom-variant dark`).
 *
 * Le choix est **aussi** gardé en `localStorage`, comme la langue : la
 * préférence serveur n'arrive qu'après authentification, et attendre le réseau
 * ferait clignoter la page en clair avant de basculer en sombre à chaque
 * chargement.
 */
import type { components } from "@/shared/api/generated";

/**
 * Le type vient du **contrat OpenAPI**, pas d'une liste écrite ici.
 *
 * Deux catalogues de thèmes divergeraient au premier ajout : `satisfies` fait
 * échouer la compilation si l'un s'écarte de l'autre, ce qu'aucun test à
 * l'exécution ne verrait — le serveur, lui, refuserait poliment en 422.
 */
export type Theme = NonNullable<components["schemas"]["UpdateSettingsRequest"]["theme"]>;

export const THEMES = ["system", "light", "dark"] as const satisfies readonly Theme[];

export const DEFAULT_THEME: Theme = "system";
const STORAGE_KEY = "teach.theme";

function isTheme(value: unknown): value is Theme {
  return typeof value === "string" && (THEMES as readonly string[]).includes(value);
}

/**
 * Ramène une valeur venue du serveur à un thème connu.
 *
 * `theme` est un `string` dans la réponse d'API, et non une énumération : y
 * ajouter une valeur serait un changement incompatible (docs/08 §2). Le client
 * doit donc accepter d'en recevoir une qu'il ne connaît pas — et retomber sur
 * `system` plutôt que de poser un `data-theme` inexistant, qui laisserait la
 * page sans aucun thème.
 */
export function toTheme(value: string): Theme {
  return isTheme(value) ? value : DEFAULT_THEME;
}

/** Thème mémorisé au dernier passage, ou `system` — jamais d'exception. */
export function readStoredTheme(): Theme {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isTheme(stored) ? stored : DEFAULT_THEME;
  } catch {
    // `localStorage` lève en navigation privée sur certains navigateurs. Un
    // thème indisponible ne doit pas empêcher l'application de s'afficher.
    return DEFAULT_THEME;
  }
}

/**
 * `matchMedia`, quand il existe.
 *
 * Les types du DOM le déclarent toujours présent ; ce n'est pas vrai partout —
 * jsdom ne l'implémente pas, et un `window.matchMedia(...)` direct y jetterait
 * l'application entière. Le typage explicite dit ce que le code suppose
 * vraiment, ce que le `?.` ne pouvait pas exprimer sans être signalé inutile.
 */
function colorSchemeQuery(): MediaQueryList | undefined {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)");
  } catch {
    // Les types du DOM déclarent `matchMedia` toujours présent ; ce n'est pas
    // vrai partout — jsdom ne l'implémente pas. Sans ce filet, la seule
    // question « l'utilisateur préfère-t-il le sombre ? » ferait tomber
    // l'application entière au démarrage.
    return undefined;
  }
}

function prefersDark(): boolean {
  return colorSchemeQuery()?.matches ?? false;
}

/** Traduit `system` en la valeur réellement affichée. */
export function resolveTheme(theme: Theme): Exclude<Theme, "system"> {
  if (theme === "system") {
    return prefersDark() ? "dark" : "light";
  }
  return theme;
}

/**
 * Pose le thème sur `<html>` et le mémorise.
 *
 * Idempotente : appelée au démarrage, à la lecture des préférences et après
 * chaque enregistrement, sans que l'ordre importe.
 */
export function applyTheme(theme: Theme): void {
  document.documentElement.dataset["theme"] = resolveTheme(theme);
  // `color-scheme` fait suivre les couleurs natives — barres de défilement,
  // champs de formulaire — que Tailwind ne touche pas.
  document.documentElement.style.colorScheme = resolveTheme(theme);
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Voir `readStoredTheme` : le thème s'applique quand même à cette session.
  }
}

/**
 * Applique le thème mémorisé et suit le réglage système tant qu'il vaut `system`.
 *
 * Retourne la fonction de désabonnement. Sans le suivi, un utilisateur en
 * `system` garderait le thème du chargement jusqu'au rafraîchissement suivant —
 * y compris après la bascule automatique du soir.
 */
export function startThemeSync(read: () => Theme = readStoredTheme): () => void {
  applyTheme(read());
  const query = colorSchemeQuery();
  if (query === undefined) {
    return () => undefined;
  }
  const onChange = () => {
    if (read() === "system") {
      applyTheme("system");
    }
  };
  query.addEventListener("change", onChange);
  return () => {
    query.removeEventListener("change", onChange);
  };
}
